import { useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import type { CollectionKey } from "@/components/usage-based-pricing-calculator/src/context";
import { UbpStep } from "@/components/usage-based-pricing-calculator/src/context";
import {
    useInputValues,
    useSetCollectionSelected,
    useStep,
    useSetStep
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { Link } from "@/libraries/design/components";
import { Image } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";

import { useIndustrySelectorTracking } from "../hooks/tracking";

import { messages } from "./industry-selector.messages";

const sectionStyles = css({
    paddingBlockEnd: token("space.300"),
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const subtitleStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.250")
});

const pillContainerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    gap: token("space.150")
});

const pillBaseStyles = css({
    "display": "inline-flex",
    "alignItems": "center",
    "gap": token("space.100"),
    "padding": `${token("space.075")} ${token("space.200")}`,
    "borderRadius": wacToken("corner.radius.pill"),
    "border": `1px solid ${wacToken("color.neutral.light300")}`,
    "backgroundColor": wacToken("color.neutral.light0"),
    "fontFamily": wacToken("font.charlie.text"),
    "fontSize": "14px",
    "lineHeight": "20px",
    "color": wacToken("color.primary.black"),
    "cursor": "pointer",
    "transition": "all 0.15s ease",
    "&:hover": {
        borderColor: wacToken("color.primary.blue")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    }
});

const pillSelectedStyles = css({
    backgroundColor: wacToken("color.extended.blue100"),
    borderColor: wacToken("color.primary.blue"),
    color: wacToken("color.primary.blue")
});

const pillIconStyles = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    flexShrink: 0
});

const learnMoreLinkContainerStyles = css({
    marginBlockStart: "24px"
});

const linkContainerStyles = css({
    marginBlockStart: token("space.300")
});

interface CollectionOption {
    id: string;
    label: string;
    icon: React.ReactNode;
    iconBg?: string;
    collectionKey: CollectionKey;
}

export const IndustrySelector = () => {
    const { collections } = useInputValues();
    const step = useStep();
    const setCollectionSelected = useSetCollectionSelected();
    const intl = useIntl();
    const setStep = useSetStep();
    const { fireCollectionToggledEvent } = useIndustrySelectorTracking();
    const collectionsHref = useLocalizedHref("/collections");

    const collectionOptions: CollectionOption[] = [
        {
            id: "teamwork",
            label: intl.formatMessage(messages.teamCollaboration),
            icon: (
                <Image
                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/cso0hpcp7aq4wi4a51y37225adqn16v2.svg"
                    alt=""
                    aria-hidden="true"
                    css={pillIconStyles}
                />
            ),
            collectionKey: "teamwork"
        },
        {
            id: "strategy",
            label: intl.formatMessage(messages.strategyPlanning),
            icon: (
                <Image
                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/3g2hra674b436d3leq47snvk154372k1.svg"
                    alt=""
                    aria-hidden="true"
                    css={pillIconStyles}
                />
            ),
            collectionKey: "strategy"
        },
        {
            id: "service",
            label: intl.formatMessage(messages.serviceManagement),
            icon: (
                <Image
                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/m3qrb882q44s0gv68e4g06160seq24xn.svg"
                    alt=""
                    aria-hidden="true"
                    css={pillIconStyles}
                />
            ),
            collectionKey: "service"
        },
        {
            id: "software",
            label: intl.formatMessage(messages.softwareDevelopment),
            icon: (
                <Image
                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/opi01qso1171rb1s7y587j62m3g0yevl.svg"
                    alt=""
                    aria-hidden="true"
                    css={pillIconStyles}
                />
            ),
            collectionKey: "software"
        }
    ];

    const handleToggle = useCallback(
        (option: CollectionOption) => {
            const isCurrentlySelected =
                collections[option.collectionKey].selected;
            const newSelected = !isCurrentlySelected;
            setCollectionSelected(option.collectionKey, newSelected);
            fireCollectionToggledEvent(option.collectionKey, newSelected);

            if (!isCurrentlySelected) {
                setStep(UbpStep.orgChangePercent);
            }
        },
        [
            collections,
            setCollectionSelected,
            setStep,
            fireCollectionToggledEvent
        ]
    );

    if (step < UbpStep.collections) {
        return null;
    }

    return (
        <div css={sectionStyles}>
            <Heading size="medium" as="h2">
                <span css={titleStyles}>
                    {intl.formatMessage(messages.industrySelectorTitle)}
                </span>
            </Heading>
            <p css={subtitleStyles}>
                {intl.formatMessage(messages.industrySelectorSubtitle)}
            </p>

            <div
                css={pillContainerStyles}
                role="group"
                aria-label={intl.formatMessage(messages.industrySelectorTitle)}
            >
                {collectionOptions.map((option) => {
                    const isSelected =
                        collections[option.collectionKey].selected;
                    return (
                        <button
                            key={option.id}
                            type="button"
                            css={[
                                pillBaseStyles,
                                isSelected && pillSelectedStyles
                            ]}
                            aria-pressed={isSelected}
                            onClick={() => handleToggle(option)}
                        >
                            {/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
                            <span
                                css={pillIconStyles}
                                style={{ backgroundColor: option.iconBg }}
                            >
                                {option.icon}
                            </span>
                            {option.label}
                        </button>
                    );
                })}
            </div>

            <div css={[linkContainerStyles, learnMoreLinkContainerStyles]}>
                <Link
                    type="primary"
                    showArrow={true}
                    showUnderline={false}
                    aria-label={intl.formatMessage(messages.learnMoreLink)}
                    text={intl.formatMessage(messages.learnMoreLink)}
                    href={collectionsHref}
                />
            </div>
        </div>
    );
};
